"use client";

import * as React from "react";
import Link from "next/link";
import { ChevronDown, ChevronRight, FolderTree } from "lucide-react";
import {
  FORUM_SUBJECTS,
  SCOPE_TO_SUBJECT_SLUG,
} from "@/lib/forum-subjects";
import { cn } from "@/lib/utils";

/**
 * 문제 포럼 상세 좌측 "전체 범위" 지도 (JCG-2105 후속).
 *
 * 전체 과목/범위(scope) 3단 트리를 보여주고, 범위를 누르면 그 자리에서 인라인으로 문제 목록을
 * 펼쳐 다른 과목·범위를 탐색하게 한다. 아래 "관련 문제" Section(SiblingNav)이 현재 scope 의
 * 문제 목록을 이미 보여주므로, 현재 scope 는 강조(앰버)만 하고 자동 펼침은 하지 않는다(중복 방지).
 *
 *  - 과목(데이터베이스, 소프트웨어 개발, ...): 접이식 그룹 헤더. 클릭 시 그 과목의 범위 목록을
 *    펼치고/접는다. 현재 scope 소속 과목(`SCOPE_TO_SUBJECT_SLUG[currentScope]`)은 마운트 시 자동 펼침.
 *  - 범위(논리 DB 설계, ...): 클릭 시 그 자리에서 인라인으로 문제 목록을 펼침/접힘(토글).
 *    현재 scope 는 앰버로 강조하되 문제 목록 자동 펼침은 하지 않는다.
 *  - 문제 목록: 범위를 처음 펼칠 때만 `GET /api/forum/sibling-problems?scope=<key>` 로 lazy fetch.
 *    이미 받은 scope 는 캐시(state)로 재요청을 막는다. 각 문제는 `next/link` 로 이동.
 *
 * 색상은 전역 디자인 토큰 + 브랜드 앰버만 사용해 다크모드 자동 대응(하드코딩 bg-white/gray 금지).
 * 강조는 앰버 페어(`text-amber-600 dark:text-amber-400`), 활성 인디케이터는 솔리드 `bg-amber-500`.
 */
interface CategoryMapProps {
  /** 현재 보고 있는 scope — 강조 + 소속 과목 자동 펼침 */
  currentScope: string;
  /** 현재 문제 — 펼친 범위 목록에서 강조 */
  currentProblemId: string;
}

/** 펼친 범위별 문제 목록 fetch 상태. */
type ScopeFetchStatus = "loading" | "ready" | "error";

const activeIndicatorCls =
  "absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-full bg-amber-500";

export default function CategoryMap({
  currentScope,
  currentProblemId,
}: CategoryMapProps) {
  // 현재 scope 소속 과목은 마운트 시 자동 펼침. 그 외 과목은 접힘.
  const initialSubjectSlug = SCOPE_TO_SUBJECT_SLUG[currentScope];
  const [openSubjects, setOpenSubjects] = React.useState<Set<string>>(
    () => new Set(initialSubjectSlug ? [initialSubjectSlug] : []),
  );
  // 펼친 범위 집합. 현재 scope 는 자동 펼침하지 않는다(아래 "관련 문제" Section 과 중복 방지).
  const [openScopes, setOpenScopes] = React.useState<Set<string>>(
    () => new Set<string>(),
  );

  // 펼친 범위별 문제 목록 fetch 상태. 'ready' 일 때 scopeIds 에 ids 가 있다.
  const [scopeStatus, setScopeStatus] = React.useState<
    Record<string, ScopeFetchStatus>
  >({});
  // 이미 받은 scope 의 ids — 렌더에 직접 사용(ref 로 읽으면 react-hooks/refs 위반).
  const [scopeIds, setScopeIds] = React.useState<Record<string, string[]>>({});
  // 인플라이트 가드용 ref — 이벤트 핸들러에서만 읽고, 렌더에서는 읽지 않는다.
  const inflight = React.useRef<Set<string>>(new Set());

  const toggleSubject = React.useCallback((slug: string) => {
    setOpenSubjects((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }, []);

  // 범위 펼침 토글 + (펼치는 경우) lazy fetch. setState updater 는 순수해야 하므로 fetch 같은
  // 부작용은 updater 밖에서 한다(StrictMode 이중 호출 시 중복 요청 방지). 펼침 여부는 현재
  // openScopes 로 판단하고, 캐시 미스 + 진행 중('loading') 아님일 때만 fetch — 에러('error')
  // 후 재펼침은 재시도되고, 인플라이트 중 재펼침은 중복 요청하지 않는다.
  const toggleScope = React.useCallback(
    (scopeKey: string) => {
      const isOpening = !openScopes.has(scopeKey);
      setOpenScopes((prev) => {
        const next = new Set(prev);
        if (isOpening) next.add(scopeKey);
        else next.delete(scopeKey);
        return next;
      });

      if (!isOpening) return;
      if (scopeIds[scopeKey]) return;
      if (inflight.current.has(scopeKey)) return;

      inflight.current.add(scopeKey);
      setScopeStatus((s) => ({ ...s, [scopeKey]: "loading" }));
      fetch(`/api/forum/sibling-problems?scope=${encodeURIComponent(scopeKey)}`)
        .then((res) =>
          res.ok ? res.json() : Promise.reject(new Error(String(res.status))),
        )
        .then((data: { success?: boolean; ids?: string[] }) => {
          inflight.current.delete(scopeKey);
          if (data.success && Array.isArray(data.ids)) {
            setScopeIds((prev) => ({ ...prev, [scopeKey]: data.ids! }));
            setScopeStatus((s) => ({ ...s, [scopeKey]: "ready" }));
          } else {
            setScopeStatus((s) => ({ ...s, [scopeKey]: "error" }));
          }
        })
        .catch(() => {
          inflight.current.delete(scopeKey);
          setScopeStatus((s) => ({ ...s, [scopeKey]: "error" }));
        });
    },
    [openScopes, scopeIds],
  );

  return (
    <nav aria-label="전체 과목·범위" className="flex flex-col gap-1">
      {FORUM_SUBJECTS.map((subject) => {
        const subjectOpen = openSubjects.has(subject.slug);
        return (
          <div key={subject.slug} className="flex flex-col">
            {/* 과목 헤더 — 클릭 시 범위 목록 펼침/접힘 */}
            <button
              type="button"
              onClick={() => toggleSubject(subject.slug)}
              aria-expanded={subjectOpen}
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] font-bold text-foreground transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none"
            >
              {subjectOpen ? (
                <ChevronDown
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 text-muted-foreground"
                />
              ) : (
                <ChevronRight
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 text-muted-foreground"
                />
              )}
              <FolderTree
                aria-hidden="true"
                className="h-4 w-4 shrink-0 text-muted-foreground"
              />
              <span className="truncate">{subject.name}</span>
            </button>

            {/* 범위 목록 — 들여쓰기(과목 화살표/아이콘 정렬에 맞춰 ml) */}
            {subjectOpen && (
              <ul className="ml-3 flex flex-col gap-0.5 border-l border-border pl-1.5">
                {subject.scopes.map((scope) => {
                  const isCurrent = scope.key === currentScope;
                  const scopeOpen = openScopes.has(scope.key);
                  const status = scopeStatus[scope.key];
                  return (
                    <li key={scope.key}>
                      {/* 범위 헤더 — 클릭 시 그 자리에서 문제 목록 펼침/접힘 */}
                      <button
                        type="button"
                        onClick={() => toggleScope(scope.key)}
                        aria-expanded={scopeOpen}
                        className={cn(
                          "relative flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-[12.5px] transition-colors focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none",
                          isCurrent
                            ? "font-bold text-amber-600 dark:text-amber-400"
                            : "text-muted-foreground hover:bg-accent hover:text-foreground",
                        )}
                      >
                        {isCurrent && (
                          <span className={activeIndicatorCls} aria-hidden="true" />
                        )}
                        {scopeOpen ? (
                          <ChevronDown
                            aria-hidden="true"
                            className="h-3.5 w-3.5 shrink-0"
                          />
                        ) : (
                          <ChevronRight
                            aria-hidden="true"
                            className="h-3.5 w-3.5 shrink-0"
                          />
                        )}
                        <span className="truncate">{scope.name}</span>
                      </button>

                      {/* 문제 목록 — 펼치면 lazy fetch. 로딩/에러/빈 목록/정상 처리. */}
                      {scopeOpen && (
                        <ScopeProblemList
                          scopeKey={scope.key}
                          subjectSlug={subject.slug}
                          status={status}
                          ids={scopeIds[scope.key]}
                          currentProblemId={currentProblemId}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </nav>
  );
}

interface ScopeProblemListProps {
  scopeKey: string;
  subjectSlug: string;
  status: ScopeFetchStatus | undefined;
  ids: string[] | undefined;
  currentProblemId: string;
}

/**
 * 펼친 범위 하나의 문제 목록. 캐시에 ids 가 있으면(status 가 아직 'loading' 으로 남아 있어도)
 * ids 를 우선 표시한다. 상태 전환은 부모가 aria-live 로 감싸지 않으므로 여기서 영역을
 * `aria-live="polite"` 로 감싸 스크린리더에 통지한다.
 */
function ScopeProblemList({
  scopeKey,
  subjectSlug,
  status,
  ids,
  currentProblemId,
}: ScopeProblemListProps) {
  let body: React.ReactNode;
  if (ids && ids.length > 0) {
    body = (
      <ul className="flex flex-col gap-0.5 py-0.5">
        {ids.map((id) => {
          const on = id === currentProblemId;
          return (
            <li key={id}>
              <Link
                href={`/forum/jcg/${subjectSlug}/${scopeKey}/${id}`}
                aria-current={on ? "page" : undefined}
                className={cn(
                  "relative flex items-center rounded-md px-2 py-1 font-mono text-xs transition-colors focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none",
                  on
                    ? "bg-card font-bold text-amber-600 shadow-sm dark:text-amber-400"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground",
                )}
              >
                {on && <span className={activeIndicatorCls} aria-hidden="true" />}
                <span className="truncate">{id}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    );
  } else if (ids && ids.length === 0) {
    body = (
      <p className="px-2 py-1.5 text-xs text-muted-foreground">
        이 범위에는 문제가 없어요.
      </p>
    );
  } else if (status === "error") {
    body = (
      <p className="px-2 py-1.5 text-xs text-muted-foreground">
        문제 목록을 불러오지 못했어요.
      </p>
    );
  } else {
    // loading (또는 캐시·상태 모두 미정인 첫 펼침 직후) — 스켈레톤
    body = (
      <ul className="flex flex-col gap-1 py-0.5" aria-busy="true">
        {Array.from({ length: 4 }).map((_, i) => (
          <li
            key={i}
            className="h-6 animate-pulse rounded-md bg-muted"
            aria-hidden="true"
          />
        ))}
      </ul>
    );
  }

  return (
    <div className="ml-3 border-l border-border pl-1.5" aria-live="polite">
      {body}
    </div>
  );
}
